import { useEffect } from 'react';

import { Card, CardHeader } from '../ui/Card';
import { EmptyState } from '../ui/AsyncBoundary';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { useToast } from '../ui/Overlay';
import { Can } from '../../auth/Can';
import { useResource, useMutation } from '../../hooks/useResource';
import { api } from '../../lib/api';
import * as fmt from '../../lib/format';

/**
 * What has been asked of this device, and what became of it.
 *
 * This panel is not decoration. A command travels to a vehicle over a
 * cellular link and may never arrive; without somewhere to see that, an
 * operator presses Reset, sees a toast, and has no way to distinguish
 * "rebooted" from "still sitting in a queue because the van is in a
 * basement". Every state below is one the platform can actually be in, and
 * each one says what it means in words rather than leaving the operator to
 * infer it from a colour.
 */
const STATES = {
  QUEUED: { tone: 'neutral', label: 'Queued', hint: 'Waiting for the device to connect' },
  SENT: { tone: 'brand', label: 'Sent', hint: 'Delivered to the broker, not yet acknowledged' },
  ACKED: { tone: 'good', label: 'Acknowledged', hint: 'The device confirmed it' },
  FAILED: { tone: 'critical', label: 'Failed', hint: 'The device refused it or could not be reached' },
  EXPIRED: { tone: 'warning', label: 'Expired', hint: 'Nothing collected it in time' },
  CANCELLED: { tone: 'neutral', label: 'Cancelled', hint: 'Withdrawn before it was acted on' },
};

const LABELS = {
  reboot: 'Reset',
  ota_update: 'Firmware update',
};

export function DeviceCommands({ deviceId, nonce = 0 }) {
  const toast = useToast();
  // Polls while something is in flight; the interval is short because the
  // operator is usually watching this exact row after pressing the button.
  const commands = useResource(`/api/devices/${deviceId}/commands`, { limit: 10 }, { pollMs: 15_000 });

  useEffect(() => {
    if (nonce) commands.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce]);

  const cancel = useMutation(async (commandId) =>
    api.post(`/api/devices/${deviceId}/commands/${commandId}/cancel`, {})
  );

  const rows = commands.data?.commands || [];
  if (!rows.length) {
    return (
      <Card className="overflow-hidden">
        <CardHeader title="Commands" icon="refresh" />
        <EmptyState
          icon="refresh"
          title="Nothing has been asked of this device"
          description="Resets and firmware updates appear here with what became of them."
        />
      </Card>
    );
  }

  const run = async (commandId) => {
    try {
      await cancel.run(commandId);
      toast.success('Command withdrawn.');
      commands.refetch();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader title="Commands" subtitle="Most recent first" icon="refresh" />
      <ul className="max-h-96 divide-y divide-line overflow-y-auto">
        {rows.map((c) => {
          const state = STATES[c.state] || { tone: 'neutral', label: c.state, hint: '' };
          const pending = c.state === 'QUEUED' || c.state === 'SENT';
          return (
            <li key={c.id} className="px-4 py-2.5">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-[13.5px] font-medium text-ink">
                  {LABELS[c.command] || c.command.replace(/_/g, ' ')}
                  {c.command === 'ota_update' && c.params?.version ? (
                    <span className="ml-1.5 font-normal text-ink-3">to {c.params.version}</span>
                  ) : null}
                </span>
                <Badge tone={state.tone} size="sm" icon={null}>
                  {state.label}
                </Badge>
              </div>
              <p className="mt-0.5 text-[12px] text-ink-3">
                {c.error || state.hint}
                {c.rollout_id ? ' · part of a rollout' : ''}
              </p>
              <div className="mt-1 flex items-center justify-between gap-3">
                <span className="text-[11.5px] text-ink-3" title={fmt.dateTime(c.issued_at)}>
                  {c.issued_by_email ? `${c.issued_by_email} · ` : ''}
                  {fmt.relativeTime(c.issued_at)}
                </span>
                {pending && (
                  <Can do="device:command">
                    <Button
                      variant="ghost"
                      size="sm"
                      loading={cancel.pending}
                      onClick={() => run(c.id)}
                    >
                      Cancel
                    </Button>
                  </Can>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
